/**
 * Chữ của "Thêm khách", "Lưu ý cố định" và "Nhập danh sách khách" (Đợt 2, ADR-089, 2.1 + 2.2).
 * Tiếng Việt có dấu, câu ngắn, không thuật ngữ IT. Chữ ở đây chỉ nói "khách / hồ sơ / lưu ý" — trung tính ngành.
 * Danh sách chữ chốt ở `docs/plans/stt-2-1-2-2-khach-nhap.md` mục 6.6; không thêm chữ ngoài danh sách đó.
 */
import type { MaDongNhap } from '@sapa/shared'

export interface NguCanhLyDo {
  /** Số dòng gốc của dòng được nhắc tới (trùng với dòng nào). */
  dong?: number
  /** Số mới sau khi đổi đầu số, dạng đọc to: `0389 123 456`. */
  soMoi?: string
  /** Số máy đã hiểu khi ô mất số 0 ở đầu: `01689123456`. */
  hieuLa?: string
  /** Số máy ĐOÁN (không gắn vào hồ sơ), dạng đọc to: `0389 123 456`. */
  soDoan?: string
  /** Số cũ NGUYÊN VĂN như trong sổ (`01689 123 456`) — để chủ tiệm đối chiếu với số mới. */
  soCu?: string
}

export const KH = {
  // ---- Màn Khách
  them: 'Thêm',
  themKhach: 'Thêm khách',
  themKhachPhu: 'Một khách — gõ tên và số điện thoại.',
  nhapDs: 'Nhập danh sách khách',
  nhapDsPhu: 'Dán từ Excel hoặc chọn tệp CSV.',

  // ---- Tờ Thêm / Sửa khách
  ten: 'Tên khách',
  tenGoiY: 'Ví dụ: Chị Lan',
  sdt: 'Số điện thoại',
  sdtGoiY: 'Không bắt buộc',
  luuY: 'Lưu ý cố định',
  luuYGoiY: 'Ví dụ: dị ứng thuốc, thích thợ Lan, còn nợ sổ cũ 300.000đ',
  luuYPhu: 'Hiện mỗi lần khách tới: trên lịch và lúc thu tiền.',
  conChu: (n: number) => `Còn\u00a0${n}\u00a0chữ`,
  luuYChiThem: 'Lễ tân chỉ được thêm vào lưu ý. Muốn sửa hoặc xoá, nhờ chủ tiệm hoặc quản lý.',
  themVaoLuuY: 'Thêm vào lưu ý',
  chuaCoSo: 'Chưa có số điện thoại',
  luu: 'Lưu',
  dangLuu: 'Đang lưu…',
  daThem: (ten: string) => `Đã thêm khách ${ten}.`,
  soCuDaDoi: (soMoi: string) => `Số cũ 11 số — đã đổi sang ${soMoi}.`,
  daLuu: 'Đã lưu.',
  daCoHoSo: (ten: string) => `Số này đã có hồ sơ: ${ten}. Mở hồ sơ đó?`,
  moHoSo: 'Mở hồ sơ',
  boLuuYHoi: 'Bỏ lưu ý vừa gõ? Hồ sơ đã có sẽ giữ nguyên, lưu ý bạn vừa gõ sẽ không được lưu.',
  boLuuYMoHoSo: 'Bỏ lưu ý, mở hồ sơ',
  quayLaiForm: 'Quay lại',
  suaKhach: 'Sửa khách',
  themLuuY: 'Thêm lưu ý cố định',
  chiChuDoiSo: 'Chỉ chủ tiệm hoặc quản lý đổi được số điện thoại.',
  thieuTen: 'Bạn gõ tên khách nhé.',
  soSai: 'Số điện thoại chưa đúng. Số di động có 10 chữ số, bắt đầu bằng 0.',
  luuYDai: 'Lưu ý tối đa 300 chữ.',
  mangCham: 'Mạng chậm, chưa chắc đã lưu. Bấm Lưu lại — khách không bị thêm hai lần.',

  // ---- Nhập danh sách: dán
  nhapHuongDan: 'Mở sổ khách trong Excel, chọn các cột Tên, Số điện thoại… rồi dán vào đây.',
  nhapMeo:
    'Nên chép cả dòng tiêu đề để máy hiểu đúng cột. Lần đầu, dán thử vài dòng xem đúng chưa. Mỗi lần tối đa 2.000 khách.',
  nhapLuuY: 'Chỉ nhập khách của tiệm bạn. Khách đã yêu cầu xoá dữ liệu thì đừng nhập lại.',
  oDanNhan: 'Dán danh sách vào đây',
  chonTep: 'Chọn tệp CSV',
  xemTruoc: 'Xem trước',
  dangKiem: 'Đang kiểm…',
  trong: 'Chưa có dòng nào. Bạn dán danh sách vào ô trên nhé.',
  thieuCotTen: 'Chưa thấy cột Tên. Bạn chép kèm dòng tiêu đề (Tên, Số điện thoại…) nhé.',
  quaDong: 'Mỗi lần nhập tối đa 2.000 dòng. Bạn chia làm nhiều lần nhé.',
  tepLonQua: 'Tệp lớn quá (tối đa 1 MB). Bạn chia nhỏ tệp nhé.',
  tepKhongDoc:
    'Không đọc được tệp này. Bạn lưu lại bằng Excel dạng \u201cCSV UTF-8\u201d rồi chọn lại nhé.',

  // ---- Nhập danh sách: xem trước
  cotDaHieu: (cot: string) => `Đã hiểu cột: ${cot}`,
  khongTieuDe:
    'Không thấy dòng tiêu đề — đã đoán cột Tên và Số điện thoại. Muốn nhập ngày sinh, ghi chú thì chép kèm dòng tiêu đề.',
  /** Không tiêu đề và máy KHÔNG tìm được cột số: chỉ nói đoán được cột Tên (không khẳng định cột số). */
  khongTieuDeChiTen:
    'Không thấy dòng tiêu đề — máy mới đoán được cột Tên. Muốn nhập số điện thoại, ngày sinh thì chép kèm dòng tiêu đề.',
  ghiChuThanhLuuY: 'Ghi chú trong sổ sẽ thành \u201cLưu ý cố định\u201d của khách.',
  ngayThangNam: 'Ngày trong danh sách viết kiểu tháng/ngày/năm — đã đọc theo kiểu đó.',
  khongUtf8:
    'Tệp này lưu theo bảng mã tiếng Việt của Windows — máy đã đọc theo bảng mã đó. Bạn xem kỹ dấu của tên nhé.',
  /**
   * Thẻ đếm xem trước — mỗi phần một dòng, phần 0 không hiện (vòng 36). Dòng SẼ KHÔNG nhập khác dòng máy đã sửa
   * giúp, khác dòng không có số (vẫn nhập, máy không sửa gì).
   */
  demMoi: (n: number) => `${n} khách mới`,
  demDaCo: (n: number) => `${n} đã có hồ sơ`,
  demKhongNhap: (n: number) => `${n} dòng sẽ không nhập`,
  demDaSua: (n: number) => `${n} dòng đã sửa giúp, nên xem`,
  demKhongSo: (n: number) => `${n} dòng không có số, vẫn nhập`,
  /** Tiêu đề từng nhóm trong danh sách xem trước. */
  nhomKhongNhap: (n: number) => `Sẽ không nhập (${n})`,
  nhomDaSua: (n: number) => `Máy đã sửa giúp, nên xem (${n})`,
  nhomKhongSo: (n: number) => `Không có số, vẫn nhập (${n})`,
  /** Dán nhầm bảng: không dòng nào đọc ra số. Hai câu theo việc máy có tìm được cột số hay không. */
  khongCoCotSdt: 'Chưa thấy cột số điện thoại. Kiểm tra lại cột bạn dán.',
  khongDocDuocSo: 'Không đọc được số điện thoại nào trong danh sách. Kiểm tra lại cột bạn dán.',
  chiCoTenXacNhan: 'Tiệm mình chỉ có tên khách, không có số — vẫn nhập.',
  dongSo: (i: number) => `Dòng ${i}`,
  xemKhongCanSua: (n: number) => `Xem ${n} dòng không cần sửa`,
  anKhongCanSua: 'Ẩn các dòng không cần sửa',
  xemThem: 'Xem thêm',
  khongCoTen: 'Không có tên',
  nhapKhach: (n: number) => `Nhập ${n} khách`,
  dangNhap: 'Đang nhập…',
  khongCoKhachMoi: 'Không có khách mới nào để nhập.',
  danLai: 'Dán lại',
  nhapMangCham: 'Mạng chậm, chưa chắc đã nhập xong. Bấm Nhập lại — khách không bị nhập hai lần.',
  nhapLoiChung: 'Chưa nhập được. Bạn thử lại nhé.',

  // ---- Nhập danh sách: kết quả
  daNhap: (n: number) => `Đã nhập ${n} khách.`,
  chuaNhap: (b: number) => `${b} dòng chưa nhập.`,
  tongKetQua: (tong: number, moi: number, daCo: number, chua: number) =>
    `Trong ${tong} dòng: ${moi} khách mới nhập, ${daCo} khách đã có hồ sơ nên giữ nguyên, ${chua} dòng chưa nhập.`,
  chuaNhapHuongDan: 'Các dòng dưới đây chưa được nhập. Bạn chép lại, sửa trong Excel rồi nhập lại.',
  chepChuaNhap: 'Chép danh sách dòng chưa nhập',
  daChepNut: 'Đã chép — dán vào Excel để sửa',
  khongChep: 'Máy không chép được. Bạn bôi đen đoạn dưới đây rồi chép tay nhé.',
  chuaChepCanhBao: 'Đóng là mất danh sách dòng chưa nhập. Bạn chép trước nhé.',
  vanDong: 'Vẫn đóng',
  cotChuaNhap: 'Lý do chưa nhập',
  daCoGhiKhachCu: (n: number) => `${n} khách quen đã có hồ sơ được ghi là khách cũ.`,
  chamKhachGoiY: 'Khách có \u201cLần cuối tới\u201d sẽ hiện ở Chăm khách → Lâu chưa quay lại.',
  xong: 'Xong',

  // ---- Tên cột
  tenCot: {
    ten: 'Tên',
    sdt: 'Số điện thoại',
    ngaySinh: 'Ngày sinh',
    luuY: 'Ghi chú',
    lanCuoi: 'Lần cuối tới',
  },

  /** Lý do từng dòng — một câu ngắn cho mỗi mã (`MaDongNhap` của @sapa/shared). */
  lyDo: (ma: MaDongNhap, c: NguCanhLyDo = {}): string => {
    switch (ma) {
      case 'THIEU_TEN':
        return 'Thiếu tên'
      case 'TRUNG_SO_TRONG_TEP':
        return `Trùng số với dòng ${c.dong ?? '?'}`
      case 'TRUNG_TEN_KHONG_SO':
        return `Trùng tên với dòng ${c.dong ?? '?'}, cả hai không có số di động`
      case 'DA_CO_HO_SO':
        return 'Đã có hồ sơ'
      case 'SO_CU_DOI_DAU':
        // Số cũ nguyên văn → số mới: chủ tiệm đối chiếu được với sổ (vòng 36).
        return c.soCu === undefined || c.soCu === ''
          ? `Số cũ 11 số — đã đổi sang ${c.soMoi ?? ''}`
          : `Số cũ 11 số, đã đổi: ${c.soCu} → ${c.soMoi ?? ''}`
      case 'SO_CU_THIEU_0':
        return `Số trong sổ mất số 0 ở đầu nên máy không tự đoán: khách chưa có số. Số có thể là ${c.soDoan ?? ''} — đã chép vào lưu ý`
      case 'SO_EXCEL_RUT_GON':
        return 'Số bị Excel rút gọn — đã chép vào lưu ý'
      case 'SO_MAY_BAN':
        return 'Số máy bàn — đã chép vào lưu ý'
      case 'NHIEU_SO':
        return 'Ô có nhiều số — lấy số đầu, số còn lại chép vào lưu ý'
      case 'SO_SAI':
        return 'Số chưa đúng — đã chép vào lưu ý'
      case 'KHONG_CO_SO':
        return 'Không có số điện thoại — khách sẽ khó tìm lại'
      case 'NGAY_SINH_SAI':
        return 'Ngày sinh chưa đọc được — đã chép vào lưu ý'
      case 'NGAY_SINH_THIEU_NAM':
        return 'Ngày sinh thiếu năm — đã chép vào lưu ý'
      case 'LAN_CUOI_SAI':
        return 'Lần cuối tới chưa đọc được — bỏ qua'
      case 'LAN_CUOI_TUONG_LAI':
        return 'Lần cuối tới ở sau hôm nay — bỏ qua'
      case 'LUU_Y_DAI':
        return 'Lưu ý dài quá 300 chữ — đã cắt'
      case 'TEN_DAI':
        return 'Tên dài quá 60 chữ — đã cắt'
    }
  },
} as const
